/**
 * Кирпичики шаблонов библиотеки: величины, сравнения, стороны, каркас.
 *
 * Шаблоны зеркальны: одна идея работает в обе стороны, и всё, чем
 * покупка отличается от продажи, собрано в `SIDES`. Писать каждый шаблон
 * дважды значило бы приглашать опечатку: зеркальная ветвь, у которой
 * забыли перевернуть одно условие, выглядит правильной и работает неверно.
 */
/* ------------------------------------------------------------ величины */
export const close = { kind: 'price', source: 'close' };
export const barHigh = { kind: 'price', source: 'high' };
export const barLow = { kind: 'price', source: 'low' };
export function ind(id, params, output) {
    return { kind: 'indicator', indicator: id, params, ...(output ? { output } : {}) };
}
export function number(value) {
    return { kind: 'number', value };
}
export function scaled(of, factor) {
    return { kind: 'scaled', of, factor };
}
export function previous(of, back) {
    return { kind: 'previous', of, back };
}
/** Событие произошло хоть раз за последние `bars` баров. */
export function recently(of, bars) {
    return { left: { kind: 'recent', of, bars }, operator: 'above', right: number(0) };
}
/** Событие произошло на этом баре. */
export function happened(of) {
    return { left: of, operator: 'above', right: number(0) };
}
export const above = (left, right) => ({ left, operator: 'above', right });
export const below = (left, right) => ({ left, operator: 'below', right });
export const crossUp = (left, right) => ({ left, operator: 'cross_above', right });
export const crossDown = (left, right) => ({ left, operator: 'cross_below', right });
/**
 * Крайнее значение за прошедшие бары, **без текущего**.
 *
 * Текущий бар исключён: его максимум всегда не меньше закрытия, и
 * «закрытие выше максимума за двадцать баров» не выполнилось бы никогда.
 * `lowest` — наименьшее: пробой вниз меряется от самого низкого минимума,
 * а не от самого высокого (так было раньше — и продажа срабатывала задолго
 * до настоящего пробоя).
 */
export function extremeBefore(of, bars, lowest = false) {
    return { kind: 'recent', of: previous(of, 1), bars, ...(lowest ? { lowest: true } : {}) };
}
/** Условие на другом таймфрейме: берётся последний закрытый бар того ряда. */
export function onFrame(condition, timeframe) {
    return { ...condition, timeframe };
}
/** Условие по другому рынку: BTC для альтов, индекс TOTAL, доминирование USDT. */
export function onMarket(condition, venue, symbol, timeframe) {
    return { ...condition, venue, symbol, ...(timeframe ? { timeframe } : {}) };
}
export const ema = (period) => ind('ema', { period });
export const sma = (period) => ind('sma', { period });
export const rsi = ind('rsi', { period: 14 });
export const rsiOf = (period) => ind('rsi', { period });
export const adx = ind('adx', { period: 14 });
export const rvol = ind('relative_volume', { period: 20 });
export const vwap = ind('vwap', { period: 20 });
export const atr = (period) => ind('atr', { period });
export const stochK = ind('stochastic', { period: 14, smooth: 3 }, 'k');
export const macdLine = ind('macd', { fast: 12, slow: 26, signal: 9 }, 'line');
export const macdSignal = ind('macd', { fast: 12, slow: 26, signal: 9 }, 'signal');
export const bb = (output) => ind('bollinger', { period: 20, deviations: 2 }, output);
export const premium = () => ind('premium', swingParams);
export const structureTrend = () => ind('trend_structure', swingParams);
export const swingParams = { left: 5, right: 5 };
/*
 * Окно дивергенции то же, что у структуры, — иначе «экстремум» в одном
 * условии и «экстремум» в соседнем означали бы разные точки на одном
 * графике. Предел в шестьдесят баров держит расхождение внутри движения.
 */
export const divergenceParams = { period: 14, left: 5, right: 5, maxGap: 60 };
/* Тройному расхождению места нужно больше: три вершины в 60 баров редкость. */
export const tripleParams = { ...divergenceParams, maxGap: 150 };
/*
 * Сколько баров назад считается «тут же»: подтверждение экстремума
 * занимает `right` баров, слом приходит следом — двадцати хватает на
 * последовательность и мало, чтобы притянуть недельное событие.
 */
export const TRAIL = 20;
export const SIDES = [
    { direction: 'LONG', way: 'up', sign: 1, liquidity: 'low', edge: barHigh, beyond: above, cross: crossUp },
    { direction: 'SHORT', way: 'down', sign: -1, liquidity: 'high', edge: barLow, beyond: below, cross: crossDown },
];
/** Противоположное сравнение: «в нашу сторону» наоборот. */
export const against = (side) => side.beyond === above ? below : above;
/** Противоположное пересечение. */
export const crossAgainst = (side) => side.cross === crossUp ? crossDown : crossUp;
/** Пробой крайней точки последних `bars` баров в нашу сторону. */
export function breakOut(side, bars) {
    return side.cross(close, extremeBefore(side.edge, bars, side.sign < 0));
}
/** Число «с нашей стороны»: 30 для покупок превращается в 70 для продаж. */
export function level(side, forLong, center = 50) {
    return side.sign > 0 ? forLong : 2 * center - forLong;
}
/** Цена в нужной части диапазона: для покупок — ниже `forLong` %, для продаж — выше зеркального. */
export function inZone(side, forLong) {
    return side.sign > 0 ? below(premium(), number(forLong)) : above(premium(), number(100 - forLong));
}
/* ------------------------------------------------------------- каркас */
export function spec(options, name, branches, threshold) {
    return {
        id: options.id,
        name,
        venue: options.venue,
        symbol: options.symbol,
        timeframe: options.timeframe,
        branches,
        threshold,
        version: 1,
        enabled: true,
        // Уведомления не включаются сами: звонок в телефон — отдельное решение.
        notify: false,
        createdAt: options.now,
        updatedAt: options.now,
    };
}
export const weight = (id, condition, value) => ({ id, condition, weight: value });
/** Зеркальная пара ветвей из одного описания стороны. */
export function mirror(make) {
    return SIDES.map((side) => ({ direction: side.direction, ...make(side) }));
}
/** Одна ветвь без стороны: предупреждение, а не совет. */
export function alertOnly(branch) {
    return [{ direction: 'ALERT', ...branch }];
}
//# sourceMappingURL=kit.js.map